<template>
  <overlay-scrollbars :options="scrollBarsOptions" ref="emailSuggestionListScrollbar">
    <ul class="customer-list" tabindex="0" @keydown.prevent.stop="onListKeydown" ref="listEl">
      <li
        v-for="(customer, index) in customerList"
        :key="customer.id + ' ' + index"
        ref="items"
        class="list-item"
        :class="{ active: index === activeIndex }"
        @click="selectEmail(customer, index)"
      >
        <div class="item-content">
          <div class="item-logo">
            <b-avatar :text="customer.contact_name[0]" />
          </div>
          <div class="item-text">
            <div class="item-text--name">{{ customer.name }}</div>
            <div class="item-text--email">{{ customer.email }}</div>
          </div>
        </div>
      </li>
    </ul>
  </overlay-scrollbars>
</template>

<script>
import { mapGetters } from 'vuex'
import { debounce } from '@/utils/'
import CustomerService from '@/store/services/customer.service'

export default {
  name: 'EmailSuggestionTable',
  props: {
    searchText: {
      type: String,
      default: '',
    },
    focusable: {
      type: Boolean,
      default: false,
    },
    excludeIds: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      rawCustomerList: [],
      activeIndex: -1,
      scrollBarsOptions: {
        scrollbars: { autoHide: 'never' },
        overflowBehavior: { y: 'scroll', x: 'hidden' }
      }
    }
  },
  computed: {
    ...mapGetters({
      getCompanyId: 'main/getCompanyId',
    }),
    customerList() {
      return this.rawCustomerList.filter(c => {
        const companyId = c.customer?.companysettings?.company
        return !this.excludeIds.includes(companyId)
      })
    },
  },
  watch: {
    searchText(newVal) {
      let filters = {
        search_by_email: newVal,
        owner_company: this.getCompanyId,
      }
      this.debouncedSearch(filters)
    },
  },
  created() {
    this.debouncedSearch = debounce((filters) => {
      CustomerService.list(filters).then((response) => {
        this.rawCustomerList = response.data
      })
    }, 300)
  },
  mounted () {
    this.osInstance = this.$refs.emailSuggestionListScrollbar.osInstance()
  },
  methods: {
    selectEmail(customer, index) {
      this.activeIndex = index
      this.$emit('emailSelected', customer)
    },
    focusOnList(active = null) {
      // focus on the list if focusable
      if (this.$refs.listEl) {
        this.$refs.listEl.focus()
        if (active != null) {
          this.activeIndex = active
        }
      }
    },
    onListKeydown (e) {
      if (!this.customerList.length || !this.focusable) return

      this.focusOnList()

      if (e.key === 'ArrowDown') {
        this.moveActive(1)
      } else if (e.key === 'ArrowUp') {
        this.moveActive(-1)
      } else if (e.key === 'Enter') {
        const customer = this.customerList[this.activeIndex]
        if (customer) {
          this.$emit('emailSelected', customer)
        }
      }
    },
    moveActive(delta) {
      const len = this.customerList.length
      if (!len) return

      let next = this.activeIndex + delta

      // clamp between 0 and len - 1
      if (next < 0) next = 0
      if (next >= len) next = len - 1

      this.activeIndex = next
      this.scrollToActive()
    },
        scrollToActive () {
      this.$nextTick(() => {
        const items = this.$refs.items || []
        const el = items[this.activeIndex]
        if (!el) return

        // Works fine with OverlayScrollbars: scroll the element into the viewport of the scroll container
        try {
          el.scrollIntoView({ block: 'nearest' })
        } catch (_) {
          // Fallback: use OverlayScrollbars API if needed
          const os = this.osInstance
          if (os) {
            const itemTop = el.offsetTop
            const itemBottom = itemTop + el.offsetHeight
            const { scrollTop, clientHeight } = os.elements().viewport
            if (itemTop < scrollTop) {
              os.scroll({ y: itemTop })
            } else if (itemBottom > scrollTop + clientHeight) {
              os.scroll({ y: itemBottom - clientHeight })
            }
          }
        }
      })
    },
  },
}
</script>

<style lang="scss" scoped>
ul.customer-list {
  margin: 0;
  padding: 0;
  max-height: 300px;
  overflow: visible !important;
  &:focus-visible {
    outline: none;
  }
  li.list-item {
    cursor: pointer;
    padding: 0.25rem 0.5rem;
    position: relative;
    min-height: 2rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    .item-content {
      display: flex;
      align-items: center;
      gap: 0.25rem;
      .item-text {
        display: flex;
        flex-direction: column;
        &--name, &--email {
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          display: block;
          position: relative;
        }
        &--email {
          color: $colorUnassigned;
          font-size: 0.85em;
        }
      }
    }
    &:hover, &.active { 
      background-color: #f1f1f1;
    }
  }
}
</style>